import React from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@carrotfertility/carotene-core"
import { useFormContext } from "@carrotfertility/carotene-core-x"
import { useHistory } from "react-router-dom"
import type { Action, Location } from "history"

export function UnsavedChangesPrompt(): JSX.Element {
  const history = useHistory()
  const {
    formState: { isDirty, isSubmitSuccessful }
  } = useFormContext()
  const shouldBlock = isDirty && !isSubmitSuccessful

  const [pending, setPending] = React.useState<{ location: Location; action: Action } | null>(null)
  const leaving = React.useRef(false)

  // Layout effect (not passive) so the block is torn down in the same commit that unmounts this guard,
  // before a confirmation <Redirect> runs its navigation — otherwise the guard cancels its own redirect.
  React.useLayoutEffect(() => {
    if (!shouldBlock) return undefined
    return history.block((location: Location, action: Action) => {
      if (leaving.current) return undefined
      setPending({ location, action })
      // false cancels the transition without a native confirm; the dialog decides what happens next.
      return false
    })
  }, [shouldBlock, history])

  const stay = (): void => setPending(null)

  const leave = (): void => {
    if (!pending) return
    leaving.current = true
    const { location, action } = pending
    setPending(null)
    // Replay the transition as it was attempted so the history stack stays correct. A POP delta isn't
    // recoverable from block(), so a back navigation replays as a single step.
    if (action === "POP") history.go(-1)
    else if (action === "REPLACE") history.replace(location)
    else history.push(location)
  }

  return (
    <Dialog open={pending != null} onClose={stay} aria-labelledby="unsaved-changes-title">
      <DialogTitle id="unsaved-changes-title">Leave without finishing?</DialogTitle>
      <DialogContent>
        <DialogContentText>Your answers won&apos;t be saved.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={leave}>
          Leave
        </Button>
        <Button variant="contained" color="secondary" onClick={stay}>
          Stay
        </Button>
      </DialogActions>
    </Dialog>
  )
}
